import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TopBar } from '../components/TopBar'
import { Pill } from '../components/Pill'
import { useApp } from '../store/AppContext'
import {
  DOCUMENTATION_STYLE_LABELS,
  HANDOVER_FIELDS,
  SOAP_FIELDS,
  emptyHandoverNote,
  emptySoapNote,
  isHandoverEmpty,
} from '../lib/handover'
import type { HandoverField, SoapField } from '../lib/handover'
import { shiftDateLabel } from '../lib/cover'
import { localISODate } from '../lib/date'
import { formatTime12Hour } from '../lib/time'
import type { DocumentationStyle, HandoverNote, SoapNote } from '../types'

const NO_SHIFT = ''
type IncidentSeverity = 'awareness' | 'follow_up' | 'urgent'

const INCIDENT_SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  awareness: 'For awareness',
  follow_up: 'Follow-up needed',
  urgent: 'Urgent',
}

export function Handover() {
  const {
    recordHandover,
    addUpdate,
    myLinkableShifts,
    currentUser,
    allUsers,
    accessProfile,
    permissionsForUser,
  } = useApp()
  const navigate = useNavigate()
  const todayISO = localISODate()
  const defaultShiftId = useMemo(
    () => myLinkableShifts.find((e) => e.date === todayISO)?.id ?? myLinkableShifts.find((e) => e.date < todayISO)?.id ?? NO_SHIFT,
    [myLinkableShifts, todayISO],
  )

  const [note, setNote] = useState<HandoverNote>(emptyHandoverNote)
  const [soap, setSoap] = useState<SoapNote>(emptySoapNote)
  const [useSoap, setUseSoap] = useState(false)
  const [linkedEventId, setLinkedEventId] = useState<string>(defaultShiftId)
  const [incidentWarning, setIncidentWarning] = useState(false)
  const [incidentSeverity, setIncidentSeverity] = useState<IncidentSeverity>('awareness')
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const documentationStyle: DocumentationStyle = useSoap ? 'soap' : 'standard'
  const soapPayload = useSoap ? soap : undefined
  const isAdultAccount = accessProfile.accountType === 'adult'

  const setField = (key: HandoverField, value: string) => setNote((n) => ({ ...n, [key]: value }))
  const setSoapField = (key: SoapField, value: string) => setSoap((s) => ({ ...s, [key]: value }))

  const incidentRecipients = useMemo(() => {
    if (!isAdultAccount) return []

    const nominee = allUsers.find(
      (user) => user.role === 'rep' && permissionsForUser(user.id).includes('view_handover'),
    )

    // Prototype routing: choose the authorised allied-health clinician whose
    // role/title is most likely to represent behaviour support. Production
    // data should replace this title match with an explicit participant-owned
    // "behaviour alert recipient" setting.
    const behaviouralClinician = allUsers.find((user) => {
      if (user.role !== 'allied_health') return false
      if (!permissionsForUser(user.id).includes('view_handover')) return false
      return /behavio(u)?r|\bbsp\b|psychologist/i.test(user.title ?? '')
    })

    return [nominee, behaviouralClinician]
      .filter((user): user is NonNullable<typeof user> => !!user && user.id !== currentUser.id)
      .filter((user, index, list) => list.findIndex((item) => item.id === user.id) === index)
  }, [allUsers, currentUser.id, isAdultAccount, permissionsForUser])

  const submit = () => {
    if (saved) return
    if (isHandoverEmpty(note, soapPayload)) {
      setError('Write at least one part of the handover before saving.')
      return
    }
    const ok = recordHandover({ note, documentationStyle, soap: soapPayload, linkedEventId: linkedEventId || undefined, notifyBySms: false })
    if (!ok) {
      setError('This handover could not be saved.')
      return
    }

    if (incidentWarning && isAdultAccount) {
      const detail = note.concerns.trim() || note.summary.trim() || 'Review the shift handover for details.'
      addUpdate({
        id: `incident-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        authorId: currentUser.id,
        type: 'general',
        text: `Behavioural / incident warning — ${INCIDENT_SEVERITY_LABELS[incidentSeverity]}\n${detail}`,
        createdAt: new Date().toISOString(),
        notifyBySms: false,
        taggedUserIds: incidentRecipients.map((user) => user.id),
      })
    }

    setError('')
    setSaved(true)
    setTimeout(() => navigate('/updates'), 700)
  }

  return (
    <div>
      <TopBar title="Shift Handover" subtitle="Your record of the shift you worked" />
      <div className="flex flex-col gap-4 px-4 py-4">
        <div className="rounded-2xl bg-brand-50 p-3 text-xs text-brand-700">
          Written by {currentUser.name}. Saved handovers appear in Team Updates for everyone the participant has shared handovers with.
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-card">
          <p className="mb-2 text-xs font-semibold text-slate-600">Shift this handover is about</p>
          {myLinkableShifts.length === 0 ? (
            <p className="text-xs text-slate-400">No rostered shift found for you this week. You can still write a handover — it just won’t be linked to a shift.</p>
          ) : (
            <select value={linkedEventId} onChange={(e) => setLinkedEventId(e.target.value)} className="input">
              <option value={NO_SHIFT}>Not linked to a shift</option>
              {myLinkableShifts.map((shift) => (
                <option key={shift.id} value={shift.id}>{shiftDateLabel(shift.date)} · {formatTime12Hour(shift.startTime)}–{formatTime12Hour(shift.endTime)} · {shift.title}</option>
              ))}
            </select>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-slate-600">Add SOAP note</p>
              <p className="mt-0.5 text-[11px] text-slate-400">Off by default. Turn on if the participant’s team uses Subjective / Objective / Assessment / Plan notes.</p>
            </div>
            <button type="button" role="switch" aria-checked={useSoap} aria-label="Add SOAP note" onClick={() => setUseSoap((v) => !v)} className={`mt-0.5 h-7 w-12 shrink-0 rounded-full transition ${useSoap ? 'bg-brand-600' : 'bg-slate-200'}`}>
              <span className={`block h-6 w-6 rounded-full bg-white shadow transition ${useSoap ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="mt-2"><Pill className={useSoap ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}>Saving as {DOCUMENTATION_STYLE_LABELS[documentationStyle]}</Pill></div>
        </div>

        {isAdultAccount && (
          <div className={`rounded-2xl border p-3.5 shadow-card ${incidentWarning ? 'border-amber-200 bg-amber-50/70' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-slate-700">Behavioural / incident warning</p>
                <p className="mt-1 text-[11px] leading-4 text-slate-500">
                  Flag this handover when the nominee and behavioural clinician need to be alerted.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={incidentWarning}
                aria-label="Behavioural or incident warning"
                onClick={() => setIncidentWarning((value) => !value)}
                className={`mt-0.5 h-7 w-12 shrink-0 rounded-full transition ${incidentWarning ? 'bg-amber-600' : 'bg-slate-200'}`}
              >
                <span className={`block h-6 w-6 rounded-full bg-white shadow transition ${incidentWarning ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </div>

            {incidentWarning && (
              <div className="mt-3 space-y-3">
                <div>
                  <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Alert level</p>
                  <div className="grid grid-cols-3 gap-2">
                    {(Object.keys(INCIDENT_SEVERITY_LABELS) as IncidentSeverity[]).map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setIncidentSeverity(level)}
                        className={`rounded-xl border px-2 py-2 text-[11px] font-bold ${incidentSeverity === level ? 'border-amber-500 bg-white text-amber-800' : 'border-amber-100 bg-amber-50 text-slate-600'}`}
                      >
                        {INCIDENT_SEVERITY_LABELS[level]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border border-amber-100 bg-white/80 px-3 py-2 text-[11px] leading-4 text-slate-600">
                  <span className="font-bold text-slate-700">Alert sent to: </span>
                  {incidentRecipients.length > 0
                    ? incidentRecipients.map((user) => `${user.name}${user.title ? ` (${user.title})` : ''}`).join(' · ')
                    : 'No authorised nominee or behavioural clinician is currently configured.'}
                </div>
              </div>
            )}
          </div>
        )}

        {useSoap && (
          <div className="flex flex-col gap-3 rounded-2xl border border-blue-100 bg-blue-50/50 p-3.5">
            <p className="text-xs font-bold text-blue-800">SOAP note</p>
            {SOAP_FIELDS.map((field) => (
              <label key={field.key} className="block text-xs font-semibold text-slate-600">
                <span className="mb-1 block">{field.label}</span>
                <textarea value={soap[field.key]} onChange={(e) => setSoapField(field.key, e.target.value)} rows={2} className="input" placeholder={field.placeholder} />
              </label>
            ))}
          </div>
        )}

        {HANDOVER_FIELDS.map((field) => (
          <label key={field.key} className="block text-xs font-semibold text-slate-600">
            <span className="mb-1 block">{field.label}</span>
            <textarea value={note[field.key]} onChange={(e) => setField(field.key, e.target.value)} rows={field.rows} className="input" placeholder={field.placeholder} />
          </label>
        ))}

        {error && <p className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{error}</p>}
        <button onClick={submit} className="rounded-2xl bg-brand-600 py-3 text-sm font-bold text-white shadow-card disabled:opacity-70" disabled={saved}>{saved ? 'Saved ✓' : 'Save handover'}</button>
        <p className="text-center text-[11px] text-slate-400">Blank sections are left out, so only what you wrote is shared. Marnixia uses in-app notifications only.</p>
      </div>
    </div>
  )
}

